/**
 * Address autocomplete for the `address` field — formvu-web's `GET /api/ga`
 * (the widget's Mapbox search, answered cross-origin). Not part of the backend
 * OpenAPI (`v1.d.ts`): the route answers on the web proxy itself.
 *
 * Every failure is `unavailable`, never a throw: the field then behaves as a
 * plain text box, because an address must stay typeable when search is down
 * or throttled.
 */

/**
 * The structured parts of a picked address — the `_addressComponents` sidecar
 * the widget's address screen writes, in the same shape. Downstream mapping
 * (platform `partial-mapping.ts`, `compose-street.ts`) reads it from `preData`.
 */
export interface AddressComponents {
  primary_line: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface AddressSuggestion {
  mapbox_id: string;
  /** Street line, unit included when there is one. */
  line1: string;
  /** "City, State ZIP, Country". */
  line2: string;
  /** Mapbox match confidence; medium/low are shown dimmed, like the widget. */
  confidence: AddressConfidence;
  /** The unit came from the typed text, not from a known address. */
  unit_extrapolated: boolean;
  /** The suggestion's unit ("APT 5A"), "" when it has none. */
  secondary_line: string;
  components: AddressComponents | null;
}

export type AddressConfidence = "exact" | "high" | "medium" | "low";

const CONFIDENCES: ReadonlyArray<AddressConfidence> = ["exact", "high", "medium", "low"];

export type AddressSearchResult =
  | { status: "ok"; suggestions: AddressSuggestion[] }
  | { status: "unavailable" };

export const ADDRESS_QUERY_MIN = 3;

const COMPONENT_KEYS = ["primary_line", "city", "state", "zip_code"] as const;

/** All four parts as non-empty strings, else null — a partial set would send
 *  a sidecar downstream mapping cannot use. The suggestion stays pickable;
 *  only its parts are dropped. */
function completeComponents(value: unknown): AddressComponents | null {
  if (!value || typeof value !== "object") return null;
  const parts = value as Record<string, unknown>;
  return COMPONENT_KEYS.every((key) => typeof parts[key] === "string" && (parts[key] as string).trim() !== "")
    ? {
        primary_line: parts.primary_line as string,
        city: parts.city as string,
        state: parts.state as string,
        zip_code: parts.zip_code as string,
      }
    : null;
}

/** A picked suggestion, as the form submit needs it. */
export interface AddressPick {
  /** The text the pick put in the field; a later edit makes it stale. */
  value: string;
  components: AddressComponents | null;
}

/**
 * The `_addressComponents` sidecar for this submit: the first address field
 * whose submitted text is still exactly what its picked suggestion put there.
 * One key, like the widget writes — a form with two address fields carries
 * the first one's parts. A typed-over or never-picked address sends none, so
 * downstream mapping never reads parts that describe another address.
 */
export function pickedAddressComponents(
  fields: ReadonlyArray<{ name: string; type: string }>,
  values: Record<string, unknown>,
  picks: ReadonlyMap<string, AddressPick>,
): AddressComponents | null {
  for (const field of fields) {
    if (field.type !== "address") continue;
    const pick = picks.get(field.name);
    const value = values[field.name];
    if (pick?.components && typeof value === "string" && value.trim() === pick.value.trim()) {
      return pick.components;
    }
  }
  return null;
}

/** The answer the field stores: what the visitor sees in the suggestion. */
export function suggestionLabel(suggestion: AddressSuggestion): string {
  return suggestion.line2 ? `${suggestion.line1}, ${suggestion.line2}` : suggestion.line1;
}

export async function searchAddresses(
  baseUrl: string,
  query: string,
  signal?: AbortSignal,
): Promise<AddressSearchResult> {
  const q = query.trim();
  if (q.length < ADDRESS_QUERY_MIN) return { status: "ok", suggestions: [] };
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/api/ga?q=${encodeURIComponent(q)}`, {
      signal,
      credentials: "omit",
    });
  } catch (err) {
    if ((err as { name?: string } | null)?.name === "AbortError") throw err;
    return { status: "unavailable" };
  }
  // 400 = the query itself was refused (too short / too long): nothing to
  // suggest, not an outage.
  if (response.status === 400) return { status: "ok", suggestions: [] };
  if (!response.ok) return { status: "unavailable" };
  try {
    const body = (await response.json()) as { suggestions?: unknown };
    const suggestions = Array.isArray(body.suggestions)
      ? (body.suggestions as Array<Partial<AddressSuggestion> | null>)
          .filter((s) => typeof s?.mapbox_id === "string" && typeof s?.line1 === "string")
          .map((s) => ({
            mapbox_id: s!.mapbox_id as string,
            line1: s!.line1 as string,
            line2: typeof s!.line2 === "string" ? s!.line2 : "",
            // Same default as the proxy: an unknown confidence is the lowest.
            confidence: CONFIDENCES.includes(s!.confidence as AddressConfidence)
              ? (s!.confidence as AddressConfidence)
              : "low",
            unit_extrapolated: s!.unit_extrapolated === true,
            secondary_line: typeof s!.secondary_line === "string" ? s!.secondary_line : "",
            components: completeComponents(s!.components),
          }))
      : [];
    return { status: "ok", suggestions };
  } catch {
    return { status: "unavailable" };
  }
}
